import React, {MouseEventHandler, ReactElement, ReactNode, Ref, useLayoutEffect, useRef, useState} from 'react';
import ReactDOM from "react-dom";


import {MenuAlignment, MenuPlacement, normalizePosition} from "../../../utils/normalizePosition";
import {Pointer, PointerPosition} from "../Pointer/Pointer";


import {screenSizeProvider} from "../../../stores/ScreenSizeProvider";


import './Tooltip.css'


export interface ChildBaseProps {
    ref?: Ref<any>;
    className?: string;
    mouseEnter?: MouseEventHandler;
    mouseLeave?: MouseEventHandler;
}

export interface TooltipProps<T extends ReactElement<ChildBaseProps> = ReactElement<ChildBaseProps>> {
    title: ReactNode;
    hotKeys?: ReactNode;
    children: T;
    menuPlacement?: MenuPlacement;
    menuAlignment?: MenuAlignment;
    VERTICAL_SHIFT?: number,
    HORIZONTAL_SHIFT?: number,
    withPointer?: boolean,
    isDark?: boolean,
    id?: string,
    isInBox?: boolean,
    maxWidth?: number,
}


// Lazy + memoized: resolved on first render inside a browser, not at module import time.
// Components that render a JSX cell containing <Tooltip> (e.g. a DataSource's
// mapResultToTableRow) transitively import this module, and Node-based tests instantiate
// those DataSources without a `window` — a module-scope `window.document...` call here would
// crash the import itself, before any Tooltip ever renders.
let _tooltipRootHTML: HTMLElement | null = null;
const getTooltipRootHTML = (): HTMLElement => {
    if (!_tooltipRootHTML) _tooltipRootHTML = window.document.getElementById('tooltip')!;
    return _tooltipRootHTML;
};


export interface MenuPosition {
    actualDirection: MenuPlacement,
    style: {},
    pointer?: PointerPosition;
}

export const Tooltip = (
    {
        title,
        hotKeys,
        menuPlacement = "block-start",
        menuAlignment = 'center',
        VERTICAL_SHIFT = 2,
        HORIZONTAL_SHIFT = 1,
        withPointer,
        children,
        isDark = true,
        isInBox = true,
        maxWidth
    }: TooltipProps
) => {

    const [showTooltip, setShowTooltip] = useState(false);
    const [menuPosition, setMenuPosition] = useState<MenuPosition | undefined>(undefined);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const childRef = useRef<HTMLDivElement>(null);
    const enterTimeoutRef = useRef<number | undefined>(undefined);
    const leaveTimeoutRef = useRef<number | undefined>(undefined);
    const previousTargetPosition = useRef<string | undefined>(undefined)
    const verticalShift = isInBox ? VERTICAL_SHIFT + 4 : VERTICAL_SHIFT;
    const isMobile = screenSizeProvider.view !== 'desktop';

    const mouseEnter = (e: React.MouseEvent) => {
        enterTimeoutRef.current = setTimeout(() => {
            setShowTooltip(true);
        }, 500) as unknown as number;
        if (leaveTimeoutRef.current !== undefined) {
            clearTimeout(leaveTimeoutRef.current);
        }
    };

    const onTooltipMouseEnter = (e: React.MouseEvent) => {
        if (leaveTimeoutRef.current !== undefined) {
            clearTimeout(leaveTimeoutRef.current);
        }
    };

    const mouseLeave = () => {
        previousTargetPosition.current = undefined;
        if (enterTimeoutRef.current !== undefined) {
            clearTimeout(enterTimeoutRef.current);
        }
        leaveTimeoutRef.current = setTimeout(() => {
            setShowTooltip(false);
            setMenuPosition(undefined);
        }, 500) as unknown as number;
    };

    useLayoutEffect(() => {
        if (tooltipRef.current && childRef.current) {
            const targetBoundingBox = childRef.current.getBoundingClientRect();
            const currentTargetPosition = `x${targetBoundingBox.x}y${targetBoundingBox.y}`;
            const targetHadMoved = previousTargetPosition.current && (previousTargetPosition.current !== currentTargetPosition);
            if (targetHadMoved) {
                mouseLeave();
                return;
            }
            previousTargetPosition.current = currentTargetPosition;
            const tooltipRootHTML = getTooltipRootHTML();
            const newMenuPosition = normalizePosition({
                contextMenu: tooltipRef.current,
                placement: menuPlacement,
                alignment: menuAlignment,
                withPointer: withPointer,
                targetBoundingBox: targetBoundingBox,
                VERTICAL_SHIFT: verticalShift,
                HORIZONTAL_SHIFT: HORIZONTAL_SHIFT,
                getScopeBoundingClientRect: tooltipRootHTML.getBoundingClientRect.bind(tooltipRootHTML),
                isPointerLarge: false,
            });

            let {pointerPosition} = newMenuPosition;
            setMenuPosition({
                actualDirection: newMenuPosition.actualDirection,
                style: {
                    top: `${newMenuPosition.top}px`,
                    left: `${newMenuPosition.left}px`,
                },
                pointer: pointerPosition,
            });
        }
    }, [showTooltip, children, title]);

    const childrenProps = {
        ref: childRef,
        onMouseEnter: mouseEnter,
        onMouseLeave: mouseLeave,
    };

    if (isMobile || !title) {
        return children;
    }

    return (
        <>
            {React.isValidElement(children) && React.cloneElement(children, childrenProps)}
            {showTooltip &&
                ReactDOM.createPortal(
                    <>
                        <div
                            onMouseEnter={onTooltipMouseEnter}
                            onMouseLeave={mouseLeave}
                            ref={tooltipRef}
                            className='Tooltip theme-dark label-medium'
                            style={{
                                ...menuPosition?.style,
                                maxWidth: maxWidth
                            }}
                        >
                            <div className="Tooltip-Title">
                                {title}
                            </div>
                            <div className="Tooltip-HotKeys">
                                {hotKeys}
                            </div>
                        </div>
                        {
                            menuPosition?.pointer
                                ? (
                                    <Pointer
                                        isDark={isDark}
                                        isRaised={true}
                                        {...menuPosition.pointer}
                                    />
                                )
                                : null
                        }
                    </>,
                    getTooltipRootHTML()
                )
            }
        </>
    )
};
